import { ActionIcon, Badge } from '@mantine/core';
import { Pencil, TriangleAlert } from 'lucide-react';
import { FC } from 'react';

import { AvailabilityStatus } from '@/models/appointment/Appointment.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';
import { cn } from '@/utils/styleUtils.ts';

const AVAILABILITY_STATUS_CONFIG = {
    [AvailabilityStatus.AVAILABLE]: {
        circleClass: 'border-green-500 text-green-600',
        rowClass: 'bg-green-50',
        badge: undefined,
    },
    [AvailabilityStatus.PROPOSED]: {
        circleClass: 'border-yellow-500 text-yellow-600 bg-yellow-50',
        rowClass: 'bg-yellow-50',
        badge: { label: 'Vorschlag', color: 'yellow' },
    },
    [AvailabilityStatus.UNAVAILABLE]: {
        circleClass: 'border-slate-300 text-slate-500',
        rowClass: 'bg-red-50',
        badge: { label: 'Konflikt', color: 'red' },
    },
};

interface Props {
    position: number;
    startDatetime: string;
    endDatetime: string;
    status: AvailabilityStatus;
    originalTime?: string;
    onEdit?: () => void;
    disabled?: boolean;
}

const CollectionCard: FC<Props> = ({
    position,
    disabled,
    startDatetime,
    endDatetime,
    status,
    originalTime,
    onEdit,
}) => {
    const config = AVAILABILITY_STATUS_CONFIG[status];
    const start = dayjs(startDatetime);
    const end = dayjs(endDatetime);
    const duration = end.diff(start, 'minute');

    return (
        <div
            className={cn(
                'flex w-full items-center justify-between border-b border-slate-200 px-4 py-3 last:border-b-0',
                config.rowClass,
                disabled && 'pointer-events-none select-none bg-slate-100 text-slate-400 opacity-50'
            )}
            aria-disabled={disabled}
        >
            {/* Left: circle + date + badge */}
            <div className={cn('flex flex-nowrap items-center gap-4', disabled && 'text-slate-400')}>
                <div
                    className={cn(
                        'flex h-7 shrink-0 items-center justify-center rounded-full border-2 px-3 text-sm font-medium',
                        config.circleClass,
                        disabled && 'border-slate-300 bg-slate-200 text-slate-400'
                    )}
                >
                    {position + 1}
                </div>

                <div className={cn('flex min-w-64 flex-col', disabled && 'text-slate-400')}>
                    <span className="font-medium">{start.format('dddd, D. MMMM YYYY')}</span>
                    <div className="flex items-center gap-1 text-sm text-slate-600">
                        <span>
                            {start.format('HH:mm')} · {duration} min
                        </span>
                        {originalTime && status === AvailabilityStatus.PROPOSED && (
                            <span className="text-orange-500">(statt {originalTime})</span>
                        )}
                    </div>
                </div>

                {config.badge && (
                    <Badge
                        color={config.badge.color}
                        variant="filled"
                        leftSection={<TriangleAlert className={cn('size-3', disabled && 'text-slate-400')} />}
                        className={disabled ? 'opacity-70' : ''}
                    >
                        {config.badge.label}
                    </Badge>
                )}
            </div>

            {/* Right: edit button */}
            {!disabled && onEdit && (
                <ActionIcon variant="subtle" color="gray" onClick={onEdit} aria-label="Termin bearbeiten">
                    <Pencil className="size-4" />
                </ActionIcon>
            )}
        </div>
    );
};

export default CollectionCard;
